import { useCallback, useEffect, useMemo, useRef } from 'react'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { EVENT_NAME_ONLINE } from '../../../../constants/event'
import { useConstant } from '../../../../hooks'
import { CENTRIFUGE_INITIAL_CONNECT_TIMEOUT_MS } from '../../Centrifuge.constants'
import {
  consoleWarnCentrifuge,
  createCentrifugeClient,
  logCentrifuge,
  safeDisconnectClient,
} from '../../Centrifuge.utils'
import { useCentrifugeDebug } from '../useCentrifugeDebug'

/**
 * Owns the single Centrifuge client and a ref count of opted-in consumers.
 * The connection opens on the first opt-in via `requestConnection` and closes
 * when the last consumer drops out via `releaseConnection`, so every realtime
 * feature shares one WebSocket.
 *
 * Initial-connect resilience: rather than giving up on the first error, we let
 * the SDK retry (its own backoff) for `CENTRIFUGE_INITIAL_CONNECT_TIMEOUT_MS`.
 * Only if nothing connects within that window do we stop the client — so a
 * transient blip at startup self-heals instead of disabling realtime for the
 * whole session. If we do give up, an `online` listener retries without a
 * reload. Reconnects *after* a successful connect are left entirely to the SDK.
 */
export function useCentrifuge({ outboundUuid } = EMPTY_OBJECT) {
  const client = useConstant(() => createCentrifugeClient({ outboundUuid }))

  const enabledCountRef = useRef(0)
  const hasConnectedRef = useRef(false)
  const connectionFailedRef = useRef(false)
  const connectTimeoutRef = useRef(null)

  const clearConnectTimeout = useCallback(() => {
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current)
      connectTimeoutRef.current = null
    }
  }, [])

  const giveUp = useCallback(() => {
    connectionFailedRef.current = true

    clearConnectTimeout()
    safeDisconnectClient(client)

    consoleWarnCentrifuge(
      `initial connection did not succeed within ${CENTRIFUGE_INITIAL_CONNECT_TIMEOUT_MS}ms; realtime disabled until connectivity is restored`
    )
  }, [clearConnectTimeout, client])

  const startConnecting = useCallback(() => {
    if (client) {
      connectionFailedRef.current = false
      clearConnectTimeout()

      try {
        client.connect()
      } catch (error) {
        consoleWarnCentrifuge('client connect error', error)

        return
      }

      if (!hasConnectedRef.current) {
        connectTimeoutRef.current = setTimeout(
          giveUp,
          CENTRIFUGE_INITIAL_CONNECT_TIMEOUT_MS
        )
      }
    }
  }, [clearConnectTimeout, client, giveUp])

  const requestConnection = useCallback(() => {
    if (client) {
      enabledCountRef.current += 1

      if (enabledCountRef.current === 1 && !connectionFailedRef.current) {
        startConnecting()
      }
    }
  }, [client, startConnecting])

  const releaseConnection = useCallback(() => {
    if (client && enabledCountRef.current > 0) {
      enabledCountRef.current -= 1

      if (enabledCountRef.current === 0) {
        // No consumer wants realtime anymore — clear the failed state so the
        // next opt-in gets a fresh attempt rather than staying stuck.
        connectionFailedRef.current = false
        clearConnectTimeout()
        safeDisconnectClient(client)
      }
    }
  }, [clearConnectTimeout, client])

  useEffect(() => {
    if (client) {
      function handleConnected() {
        hasConnectedRef.current = true
        connectionFailedRef.current = false
        clearConnectTimeout()
      }

      /*
       * If we gave up but consumers still want realtime, retry when the device
       * regains connectivity — the SDK can't self-heal here because `giveUp`
       * disconnected the client. Pending subscriptions reactivate on their own
       * once the connection is re-established.
       */
      function handleOnline() {
        if (connectionFailedRef.current && enabledCountRef.current > 0) {
          logCentrifuge('connectivity restored; retrying realtime connection')
          startConnecting()
        }
      }

      client.on('connected', handleConnected)
      window.addEventListener(EVENT_NAME_ONLINE, handleOnline)

      return () => {
        client.off('connected', handleConnected)
        window.removeEventListener(EVENT_NAME_ONLINE, handleOnline)
      }
    }
  }, [client])

  useCentrifugeDebug({ client, hasConnectedRef })

  useEffect(() => {
    return () => {
      if (client && enabledCountRef.current > 0) {
        clearConnectTimeout()
        safeDisconnectClient(client)

        enabledCountRef.current = 0
      }
    }
  }, [client])

  return useMemo(
    () => ({
      client,
      releaseConnection,
      requestConnection,
    }),
    [client, releaseConnection, requestConnection]
  )
}
